import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  MoreVertical,
  UserPlus,
  Trash2,
  Wrench,
  CheckCircle2,
  Receipt,
  FileCheck,
  Calendar as CalendarIcon
} from 'lucide-react';
import { openInNewTab } from '@/lib/openInNewTab';

export default function JobCardMenu({ card, user, mechanics, onFetchMechanics, onUpdateDeliveryDue, onAssignMechanic, onDelete, onCreateInvoice }) {
  const handleOpenChange = (open) => {
    if (open && mechanics.length === 0) {
      onFetchMechanics();
    }
  };
  
  // Only super_admin and service_manager can delete
  const canDelete = user && user.role === 'super_admin';

  const hasInvoice = !!card.invoice_id;

  // iter138.9v — Delivery Due (date+time) replaces the old Low/Medium/
  // Urgent priority items. SM/FM uses a single datetime-local input to
  // set when the car must leave. Stored on the JC as `delivery_due_at`.
  // Convert the stored ISO string to the `<input type="datetime-local">`
  // value format (YYYY-MM-DDTHH:mm). Empty when not set.
  const deliveryDueValue = (() => {
    const raw = card.delivery_due_at;
    if (!raw) return '';
    // If already in naive YYYY-MM-DDTHH:mm format, use as-is.
    const naive = raw.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/);
    if (naive) return raw.slice(0, 16);
    // Otherwise legacy full ISO (with TZ) → parse and serialise to local naive.
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      );
    } catch (_e) { return ''; }
  })();

  const handleDeliveryDueChange = (e) => {
    const v = e.target.value;  // e.g. "2026-02-20T14:00"
    // Send the value AS-IS (no TZ conversion). Storing the naïve local
    // datetime keeps SM/FM intent intact regardless of their browser TZ
    // (Playwright UTC vs real Melbourne SM was shifting by 11 h).
    onUpdateDeliveryDue?.(card.job_card_id, v || null);
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`jc-menu-trigger-${card.job_card_id}`}>
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Invoice action */}
        {hasInvoice ? (
          <>
            <DropdownMenuItem
              onClick={() => openInNewTab(`/invoices/${card.invoice_id}`)}
              className="text-emerald-700 font-medium"
              data-testid={`jc-menu-view-invoice-${card.job_card_id}`}
            >
              <FileCheck className="h-4 w-4 mr-2" />
              View Invoice
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : (
          ['pickup', 'in_progress', 'awaiting_parts', 'sm_review', 'completed'].includes(card.status) && (
            <>
              <DropdownMenuItem
                onClick={() => onCreateInvoice(card)}
                className="text-green-600 font-medium"
                data-testid={`jc-menu-create-invoice-${card.job_card_id}`}
              >
                <Receipt className="h-4 w-4 mr-2" />
                Create Invoice
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )
        )}
        
        {/* iter138.9v — Delivery Due date+time picker replaces the old
            Low/Medium/Urgent priority items. We intercept onSelect on
            this row so opening the native datetime picker doesn't close
            the menu. */}
        <div
          className="px-2 py-1.5 text-xs font-medium text-slate-500"
          onClick={(e) => e.stopPropagation()}
        >
          Delivery Due
        </div>
        <div
          className="px-2 pb-2"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1.5 rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
            <CalendarIcon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
            <input
              type="datetime-local"
              value={deliveryDueValue}
              onChange={handleDeliveryDueChange}
              className="text-xs bg-transparent outline-none flex-1 min-w-0"
              data-testid={`jc-menu-delivery-due-${card.job_card_id}`}
            />
            {deliveryDueValue && (
              <button
                type="button"
                onClick={() => onUpdateDeliveryDue?.(card.job_card_id, null)}
                className="text-[10px] text-rose-600 hover:underline shrink-0"
                title="Clear delivery due"
                data-testid={`jc-menu-delivery-due-clear-${card.job_card_id}`}
              >
                clear
              </button>
            )}
          </div>
        </div>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem className="font-medium text-xs text-slate-500 cursor-default">
          Assign Mechanic
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAssignMechanic(card.job_card_id, null)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Unassigned
          {!card.assigned_to && <CheckCircle2 className="h-4 w-4 ml-auto text-green-600" />}
        </DropdownMenuItem>
        {mechanics.map(m => (
          <DropdownMenuItem 
            key={m.user_id}
            onClick={() => onAssignMechanic(card.job_card_id, m.user_id)}
          >
            <Wrench className="h-4 w-4 mr-2" />
            {m.name}
            {card.assigned_to === m.user_id && <CheckCircle2 className="h-4 w-4 ml-auto text-green-600" />}
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator />
        
        {canDelete && (
          <DropdownMenuItem 
            onClick={() => onDelete(card.job_card_id, card.job_card_number)}
            className="text-red-600"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
